import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, Injectable, NgZone } from '@angular/core';
import { LoggerService } from './services/logger.service';

@Injectable()
export class CustomErrorHandlerService extends ErrorHandler {
    private static readonly RESET_INTERVAL_MS = 60000;
    private loggedErrors = 0;
    private lastErrorTime = 0;

    constructor(
        private loggerService: LoggerService,
        private zone: NgZone
    ) {
        super();
    }

    handleError(error) {
        if (error === undefined || error === null) {
            return;
        }

        const now = Date.now();

        if (now - this.lastErrorTime > CustomErrorHandlerService.RESET_INTERVAL_MS) {
            this.loggedErrors = 0;
        }

        this.lastErrorTime = now;

        if (this.loggedErrors > 5) {
            return;
        }

        this.loggedErrors++;

        this.zone.run(() => {
            const httpError = error.error instanceof HttpErrorResponse ? error.error : error;
            const message =
                httpError.status === 0
                    ? 'Request failed without an accessible HTTP response.'
                    : error?.message || httpError.message || this.stringifyError(error);
            const stack = error?.stack ?? '';

            let log = stack.includes(message) ? stack : `${message}\n${stack}`;

            if (httpError.status != null) {
                log += httpError.status === 0 ? '\nHTTP status: unavailable' : `\nHTTP status: ${httpError.status}`;
            }

            if (httpError.url) {
                log += `\nURL: ${httpError.url.substring(0, 150)}`;
            }

            if (httpError.error != null) {
                log += `\nError details: ${this.stringifyError(httpError.error)}`;
            }

            if (error.data != null) {
                log += `\nData: ${this.stringifyError(error.data)}`;
            }

            if (httpError.context) {
                log += `\nContext: ${this.stringifyError(httpError.context)}`;
            }

            this.loggerService.logError(log);
        });

        super.handleError(error);

        throw error;
    }

    private stringifyError(error: unknown): string {
        if (typeof error === 'string') {
            return error;
        }

        try {
            return JSON.stringify(error, (key, value) => {
                if (/password|secret|token|authorization|cookie/i.test(key)) {
                    return '[REDACTED]';
                }

                if (value instanceof Error) {
                    return { name: value.name, message: value.message, stack: value.stack };
                }

                if (value instanceof ProgressEvent) {
                    return {
                        type: value.type,
                        loaded: value.loaded,
                        total: value.total,
                        lengthComputable: value.lengthComputable,
                    };
                }

                return value;
            });
        } catch {
            return String(error);
        }
    }
}
